import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { StatusDot } from '../ui/status-dot';
import {
  CustomField, Registration, formatCustomResponse, hasValue, paymentTone, paymentLabelKey,
} from '../../lib/registrations';

// Une inscription en lecture (2026-09-27) : ce que l'athlète a rempli dans le
// tunnel, tel quel. Rien ne s'édite ici — l'équipe se corrige sur la page
// Équipes, l'inscription est le reçu de ce qui a été payé.

interface Props {
  open: boolean;
  registration: Registration | null;
  fields: CustomField[];
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-2">
    <h3 className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{title}</h3>
    {children}
  </section>
);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-white/[0.06] py-1.5 text-sm last:border-b-0">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 text-right">{value}</span>
  </div>
);

export function RegistrationSheet({ open, registration: r, fields, onClose, onPrev, onNext }: Props) {
  const { t } = useTranslation();
  const label = (id: string) => fields.find((f) => f.id === id)?.label ?? id;
  const teamFields = fields.filter((f) => (f.scope ?? 'team') === 'team');
  const teamAnswers = teamFields
    .map((f) => [f.label, r?.team_custom_fields?.[f.id]] as const)
    .filter(([, v]) => hasValue(v));
  // Une réponse d'équipe dont la question a disparu du formulaire reste lisible.
  const orphanAnswers = Object.entries(r?.team_custom_fields ?? {})
    .filter(([id, v]) => hasValue(v) && !fields.some((f) => f.id === id));

  return (
    <Sheet open={open} modal={false} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" hideClose onInteractOutside={(e) => e.preventDefault()} className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[520px]">
        <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <SheetDescription className="text-xs">
              {r && [r.category_name, r.created_at ? t('registrations.sheet.registeredAt', { date: new Date(r.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) }) : null].filter(Boolean).join(' · ')}
            </SheetDescription>
            <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">{r?.team_name}</SheetTitle>
          </div>
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.previous')} disabled={!onPrev} onClick={onPrev}><ChevronUp size={16} /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.next')} disabled={!onNext} onClick={onNext}><ChevronDown size={16} /></Button>
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={onClose}><X size={16} /></Button>
        </div>

        {r && (
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
            <Section title={t('registrations.sheet.sectionPayment')}>
              <Row label={t('registrations.list.colPayment')} value={<StatusDot tone={paymentTone(r.payment_status)} label={t(paymentLabelKey(r.payment_status))} />} />
            </Section>

            <Section title={t('registrations.sheet.sectionContact')}>
              <Row label={t('registrations.list.colContact')} value={`${r.primary_contact_first_name} ${r.primary_contact_last_name}`.trim()} />
              <Row label={t('common.email')} value={<a className="underline-offset-2 hover:underline" href={`mailto:${r.primary_contact_email}`}>{r.primary_contact_email}</a>} />
              {r.primary_contact_phone && <Row label={t('common.phone')} value={r.primary_contact_phone} />}
            </Section>

            <Section title={t('registrations.sheet.sectionParticipants', { count: r.participants.length })}>
              <ol className="space-y-3">
                {[...r.participants].sort((a, b) => a.position - b.position).map((p) => {
                  const answers = Object.entries(p.custom_fields ?? {}).filter(([, v]) => hasValue(v));
                  return (
                    <li key={p.position} className="border-b border-white/[0.06] pb-3 last:border-b-0 last:pb-0">
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="font-semibold">{p.first_name} {p.last_name}</span>
                        <span className="truncate text-xs text-muted-foreground">{p.email}</span>
                      </div>
                      {answers.length > 0 && (
                        <dl className="mt-1.5 space-y-0.5 pl-3 text-xs">
                          {answers.map(([id, v]) => (
                            <div key={id} className="flex justify-between gap-3">
                              <dt className="text-muted-foreground">{label(id)}</dt>
                              <dd className="text-right">{formatCustomResponse(v)}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </li>
                  );
                })}
              </ol>
            </Section>

            {(teamAnswers.length > 0 || orphanAnswers.length > 0) && (
              <Section title={t('registrations.sheet.sectionTeamAnswers')}>
                {teamAnswers.map(([lbl, v]) => <Row key={lbl} label={lbl} value={formatCustomResponse(v)} />)}
                {orphanAnswers.map(([id, v]) => <Row key={id} label={id} value={formatCustomResponse(v)} />)}
              </Section>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
